/**
 * Die Abschluss-Karte — was am Ende eines Auftrags herausgekommen ist. (Ticket 2444, 21.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, 21.09., 08:38: *„Die Reihenfolgen von Auftrag bis hin zum Ergebnis
 * sind nicht stringent. Das gehört als saubere Zusammenfassung unten als
 * fertig, dass man draufklicken kann und zu seinem PDF oder MD-File kommt
 * oder etwas abspielen kann. … Zum Schluss sollte immer die Summe von allem
 * kommen, damit der User nur mehr das Ergebnis sieht."*
 *
 * ## Was am selben Morgen wirklich passiert war (Werkzeug-Protokoll)
 *
 *     08:24:33 analyze_video · 08:24:34 memory_search · …
 *     08:36:24 deliver_text          ← das Skript, mitten im Zug
 *     08:38:48 memory_search, ticket_list_open
 *     08:39:39 project_file_create   ← die eigentliche Datei — als
 *                                       zugeklappte Zeile „Projektdatei angelegt"
 *
 * `project_file_create` steht seit dem 06.09. auf der Schritt-Liste
 * (schritte.ts) — richtig für die Zeile, aber die DATEI verschwand damit als
 * Ergebnis. Hier wird sie am Ende wieder sichtbar, ohne die Zeile anzurühren.
 *
 * ## Die Regel
 *
 * Eine Abschluss-Karte gibt es, wenn der Zug **mehr als ein Werkzeug** hatte
 * und **mindestens ein Ergebnis** dabei war. Ein Zug mit genau einem
 * `deliver_text` braucht keine — die Karte des Werkzeugs IST dann schon das
 * Ende. Zwei Karten für dasselbe wären die Wiederholung, die Martin an der
 * /wartet-Seite gerade erst moniert hat.
 */

import { ENDE_WERKZEUG } from "./gespraech-ende.js";

export type Ergebnis =
  | { art: "datei"; titel: string; projectId: string; fileId: string }
  | { art: "link"; titel: string; url: string }
  | { art: "text"; titel: string; text: string };

/**
 * Werkzeuge, die ein Ergebnis liefern — im Gegensatz zu Schritten (nachsehen,
 * suchen, lesen). Positivliste: ein neues Werkzeug taucht erst hier auf, wenn
 * jemand weiss, was es liefert.
 */
export const ERGEBNIS_WERKZEUGE: ReadonlySet<string> = new Set([
  "project_file_create",
  "project_file_update",
  "deliver_text",
  "make_pdf",
  "narrate_to_mp3",
  "book_compile",
  "drucksache_erstellen",
  "generate_image",
  "edit_image",
]);

type Roh = Record<string, unknown>;

function text(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Was ein Werkzeug-Ergebnis für den Menschen hergibt — oder nichts. */
export function ergebnisAus(name: string, result: unknown): Ergebnis | null {
  if (!ERGEBNIS_WERKZEUGE.has(name)) return null;
  let r: Roh;
  try {
    r = (typeof result === "string" ? JSON.parse(result) : result) as Roh;
  } catch {
    return null;
  }
  if (!r || typeof r !== "object" || r.ok === false || r.error) return null;

  if (name === "project_file_create" || name === "project_file_update") {
    const projectId = text(r.projectId);
    const fileId = text(r.id);
    const titel = text(r.title) || "Datei";
    if (projectId && fileId) return { art: "datei", titel, projectId, fileId };
    return null;
  }
  if (name === "deliver_text") {
    const t = text(r.text);
    if (!t) return null;
    return { art: "text", titel: text(r.title) || "Text", text: t };
  }
  const url = text(r.url) || text(r.link) || text(r.mp3Url);
  if (url) return { art: "link", titel: text(r.title) || text(r.name) || name, url };
  return null;
}

/**
 * Zählt dieses Werkzeug als Arbeit des Zugs? (Ticket 2701, 27.09.2026)
 *
 * `gespraech_beenden` arbeitet nicht, es meldet nur den Abschied. Zählte es
 * mit, machte „leg die Notiz an, danke, tschüss“ aus EINEM Arbeitsschritt
 * zwei, und die Abschluss-Karte käme als Wiederholung der Notiz-Karte.
 * Beide Wege (Sprechtaste und Chat in app.ts) fragen hier, bevor sie zählen.
 */
export function zaehltAlsArbeit(werkzeug: string): boolean {
  return werkzeug !== ENDE_WERKZEUG;
}

/**
 * ⚠️ Mehr als ein Werkzeug UND ein Ergebnis. Bei genau einem Werkzeug ist
 *    dessen Karte das Ende — eine zweite Karte wäre Wiederholung.
 */
export function brauchtAbschluss(werkzeugAufrufe: number, ergebnisse: readonly Ergebnis[]): boolean {
  return werkzeugAufrufe >= 2 && ergebnisse.length > 0;
}

/** Dasselbe Ergebnis zweimal (etwa update nach create) zählt einmal. */
export function ohneDoppelte(ergebnisse: readonly Ergebnis[]): Ergebnis[] {
  const gesehen = new Set<string>();
  const raus: Ergebnis[] = [];
  for (const e of ergebnisse) {
    const k = e.art === "datei" ? `d:${e.projectId}/${e.fileId}` : e.art === "link" ? `l:${e.url}` : `t:${e.titel}:${e.text.length}`;
    if (gesehen.has(k)) continue;
    gesehen.add(k);
    raus.push(e);
  }
  return raus;
}
